import {
  ROWS_PER_PAGE_OPTIONS,
  SelectDropdown
} from "@/src/sb/components/Pagination/CursorPagination"
import ArrowLeft from "@/src/sb/components/icon/ArrowLeft"
import classNames from "classnames"
import { useMemo } from "react"
import Skeleton from "react-loading-skeleton"
import tinycolor from "tinycolor2"

export type PaginationType =
  | "reports"
  | "datasets"
  | "deals"
  | "records"
  | "transactions"
  | "companies"
  | "limited partners"
  | "funds"
  | "general partners"

export type NumberedPaginationProps = {
  type?: PaginationType
  totalNumberOfRecords: number
  limit: number
  onLimitChange?: (limit: number) => void
  showRowsPerPageSelector?: boolean
  currentPageNumber?: number
  onPageClick?: (pageNumber: number) => void
  onPrevClick?: (pageNumber: number) => void
  onNextClick?: (pageNumber: number) => void
}

const NumberedPagination = ({
  currentPageNumber,
  limit,
  onLimitChange,
  totalNumberOfRecords,
  onPageClick,
  onPrevClick,
  onNextClick,
  type = "records",
  showRowsPerPageSelector = false
}: NumberedPaginationProps) => {
  const totalNumberOfPages = Math.ceil(totalNumberOfRecords / limit)
  const computedCurrentPageNumber = !currentPageNumber
    ? 1
    : Math.min(Math.abs(currentPageNumber), totalNumberOfPages)
  const pageLastRecordNumber = computedCurrentPageNumber * limit
  const endsPageNumberCount = 5
  const currentPageNumberSiblingCount = 1

  const commonButtonClassName =
    "h-full flex justify-center items-center gap-2 hover:bg-gray-100 dark:hover:bg-[#4B5563]"
  const disabledButtonClassName =
    "h-full flex justify-center items-center gap-2 cursor-not-allowed px-2.5 !text-gray-300 dark:!text-gray-400"

  // https://dev.to/namirsab/comment/2050
  const range = (start: number, end: number) => {
    const length = end - start + 1
    return Array.from({ length }, (_, i) => start + i)
  }

  const paginationCanUseEllipsis =
    endsPageNumberCount + (currentPageNumberSiblingCount * 2 + 1) <
    totalNumberOfPages
  const showFullLeftSideOfPageNumbers =
    computedCurrentPageNumber < endsPageNumberCount
  const showFullRightSideOfPageNumbers =
    totalNumberOfPages - (computedCurrentPageNumber - 1) < endsPageNumberCount
  const showCenterOfPageNumbers =
    !showFullLeftSideOfPageNumbers &&
    !showFullRightSideOfPageNumbers &&
    paginationCanUseEllipsis

  const leftSideOfPageNumbers = range(
    1,
    paginationCanUseEllipsis
      ? showFullLeftSideOfPageNumbers
        ? endsPageNumberCount
        : 1
      : totalNumberOfPages
  )
  const rightSideOfPageNumbers = range(
    showFullRightSideOfPageNumbers
      ? totalNumberOfPages - (endsPageNumberCount - 1)
      : totalNumberOfPages,
    totalNumberOfPages
  )
  const centerOfPageNumbers = range(
    computedCurrentPageNumber - currentPageNumberSiblingCount,
    computedCurrentPageNumber + currentPageNumberSiblingCount
  )

  const pageNumbers = [
    ...leftSideOfPageNumbers,
    // Start ellipsis
    (showFullRightSideOfPageNumbers && paginationCanUseEllipsis) ||
    showCenterOfPageNumbers
      ? "..."
      : "",
    // Center pages
    ...(showCenterOfPageNumbers ? centerOfPageNumbers : []),
    // End Ellipsis
    (showFullLeftSideOfPageNumbers && paginationCanUseEllipsis) ||
    showCenterOfPageNumbers
      ? "..."
      : "",
    ...(paginationCanUseEllipsis ? rightSideOfPageNumbers : [])
  ]
    .filter((pageElement) => !!pageElement)
    .map((buttonLabel, idx) => {
      return (
        <li key={buttonLabel + idx.toString()} className={"w-[40px] h-6"}>
          {buttonLabel === "..." ? (
            <span
              className={"w-full h-full flex justify-center items-center gap-2"}
            >
              ...
            </span>
          ) : (
            <button
              className={classNames("w-full", commonButtonClassName, {
                "bg-gray-100 dark:bg-[#1A1D21;] text-stears_black dark:text-white":
                  buttonLabel === computedCurrentPageNumber
              })}
              onClick={() => onPageClick?.(buttonLabel as number)}
            >
              {buttonLabel}
            </button>
          )}
        </li>
      )
    })

  const disableNextButton = computedCurrentPageNumber >= totalNumberOfPages
  const disablePrevButton = computedCurrentPageNumber <= 1

  const prevButtonContent = (
    <>
      <span>
        <ArrowLeft size={12} />
      </span>
      <span className={"hidden md:inline-block"}>Previous</span>
    </>
  )

  const nextButtonContent = (
    <>
      <span className={"hidden md:inline-block"}>Next</span>
      <span className={"rotate-180"}>
        <ArrowLeft size={12} />
      </span>
    </>
  )

  const dataIsLoaded = !!pageNumbers.length
  const skeletonColor = "#2D3036"
  const skeletonHighlighterColor = useMemo(
    () => tinycolor(skeletonColor).lighten(5).toString(),
    []
  )

  return (
    <section
      role={"navigation"}
      className={
        "py-2.5 px-5 flex justify-center items-center font-jakarta text-[12px] leading-5 text-gray-500 dark:text-gray-400"
      }
    >
      {dataIsLoaded ? (
        <section className={"inline-flex items-center gap-3 mr-auto"}>
          {showRowsPerPageSelector && (
            <section className={"hidden md:flex gap-2.5 items-center"}>
              <span>
                <span className="capitalize">
                  {type?.replace("records", "rows")}
                </span>{" "}
                per page
              </span>

              <SelectDropdown
                options={ROWS_PER_PAGE_OPTIONS}
                selected={limit}
                onSelect={({ value }) => {
                  onLimitChange?.(Number(value))
                }}
              ></SelectDropdown>
            </section>
          )}

          <span className={"hidden md:inline-block grow mr-auto"}>
            {Math.max(0, pageLastRecordNumber - (limit - 1))}-
            {Math.min(totalNumberOfRecords, pageLastRecordNumber)} of{" "}
            {totalNumberOfRecords} {type}
          </span>
        </section>
      ) : (
        <Skeleton
          containerClassName={"inline-block grow mr-auto"}
          borderRadius={0}
          width={80}
          baseColor={skeletonColor}
          highlightColor={skeletonHighlighterColor}
        />
      )}

      <section>
        {dataIsLoaded ? (
          <ul
            className={
              "flex items-center divide-x-[0.5px] dark:divide-[#4B5563] border-[0.5px] dark:border-[#4B5563]"
            }
          >
            <li className={"h-6"}>
              <button
                className={classNames(
                  `text-gray-500 dark:text-white px-2.5`,
                  disablePrevButton
                    ? disabledButtonClassName
                    : commonButtonClassName
                )}
                disabled={disablePrevButton}
                onClick={() => {
                  const pageNumber = computedCurrentPageNumber - 1
                  onPageClick?.(pageNumber)
                  onPrevClick?.(pageNumber)
                }}
              >
                {prevButtonContent}
              </button>
            </li>

            {pageNumbers}

            <li className={"h-6"}>
              <button
                className={classNames(
                  `text-gray-500 dark:text-white px-2.5`,
                  disableNextButton
                    ? disabledButtonClassName
                    : commonButtonClassName
                )}
                disabled={disableNextButton}
                onClick={() => {
                  const pageNumber = computedCurrentPageNumber + 1
                  onPageClick?.(pageNumber)
                  onNextClick?.(pageNumber)
                }}
              >
                {nextButtonContent}
              </button>
            </li>
          </ul>
        ) : (
          <Skeleton
            containerClassName={"inline-block"}
            borderRadius={0}
            width={160}
            baseColor={skeletonColor}
            highlightColor={skeletonHighlighterColor}
          />
        )}
      </section>
    </section>
  )
}

export default NumberedPagination
